import React from "react"
import { Checkbox, TextInput, Group } from "@mantine/core"
import { Button } from "@quorumus/plum"
import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/OptionsAdder.module.css"
import { Icon } from "@/components"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import ReactDOM from "react-dom"
import { getOrCreateDivPortal } from "@/components/CampaignDragAndDrop/helper"
import { useForm } from "@/pages/hooks/useForm"
import { QuestionForm } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/CreateQuestionModal"

interface Option {
    order: number
    value: string
    archived: boolean
    slug?: string
}

const generateRandomAlphanumeric = (length = 6) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
    let result = ""
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
}

const OptionItem = ({
    option,
    onChangeText,
    provided,
    defaultValues,
    onCheckboxChange,
    removeOption,
}: {
    option: Option
    onChangeText: (order: number, newValue: string) => void
    provided: any
    defaultValues: string[]
    onCheckboxChange: (value: string) => void
    removeOption: (option: Option) => void
}) => {
    return (
        <Group className={classes.optionGroup} noWrap>
            <Button variant="tertiary" className={classes.removeButton} {...provided.dragHandleProps}>
                <Icon icon="grip-vertical" className={classes.dragIcon} iconFamily="fas" />
            </Button>
            <TextInput
                className={classes.input}
                value={option.value}
                onChange={(e) => onChangeText(option.order, e.target.value)}
                placeholder="Option name"
                rightSection={
                    <Checkbox
                        checked={option.value && defaultValues.includes(option.value)}
                        onChange={() => onCheckboxChange(option.value)}
                        className={classes.checkbox}
                        label="Default"
                    />
                }
                rightSectionWidth={100}
            />
            <Button variant="tertiary" className={classes.removeButton} onClick={() => removeOption(option)}>
                <Icon icon="times" />
            </Button>
        </Group>
    )
}

const DragLayer = ({ children, snapshot, provided }) => {
    if (snapshot.isDragging) {
        return ReactDOM.createPortal(
            <div {...provided.draggableProps} className={classes.dragLayer}>
                {children}
            </div>,
            getOrCreateDivPortal(),
        )
    }
    return (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{
                ...provided.draggableProps.style,
                visibility: snapshot.isDragging ? "hidden" : "visible",
            }}
        >
            {children}
        </div>
    )
}

interface Props {
    questionForm: ReturnType<typeof useForm<QuestionForm>>
    isMulti?: boolean
}

export const OptionsAdder = ({ questionForm, isMulti }: Props) => {
    const options = questionForm.values.options_new || []
    const multiDefaultValues = questionForm.values.default_multi_value || []
    const singleDefaultValue = questionForm.values.default_string_value

    const addOption = () => {
        const slug = generateRandomAlphanumeric()
        questionForm.setFieldValue("options_new", [
            ...options,
            { order: options.length, value: "", archived: false, slug },
        ])
    }

    const handleValueChange = (order: number, newValue: string) => {
        const newOptions = options.map((option) => (option.order === order ? { ...option, value: newValue } : option))
        questionForm.setFieldValue("options_new", newOptions)
    }

    const onDragEnd = (event) => {
        const startIndex = event.source.index
        const endIndex = event.destination.index

        const draggedItem = options[startIndex]
        const newOptions = [...options]
        newOptions.splice(startIndex, 1)
        newOptions.splice(endIndex, 0, draggedItem)
        newOptions.map((option, index) => (option.order = index))
        questionForm.setFieldValue("options_new", newOptions)
    }

    const toggleDefault = (value: string) => {
        if (!value) return
        if (!isMulti) {
            questionForm.setFieldValue("default_string_value", value)
            return
        }
        const newValue = multiDefaultValues.includes(value)
            ? multiDefaultValues.filter((v) => v !== value)
            : [...multiDefaultValues, value]
        questionForm.setFieldValue("default_multi_value", newValue)
    }

    const removeOption = (option: Option) => {
        const newOptions = options.filter((lastOption) => lastOption.order !== option.order)
        questionForm.setFieldValue("options_new", newOptions)
        if (!isMulti) {
            questionForm.setFieldValue(
                "default_string_value",
                singleDefaultValue === option.value ? "" : singleDefaultValue,
            )
            return
        }
        questionForm.setFieldValue(
            "default_multi_value",
            multiDefaultValues.filter((v) => v !== option.value),
        )
    }

    return (
        <DragDropContext onDragEnd={(event) => onDragEnd(event)}>
            <Droppable droppableId="droppable">
                {(provided) => (
                    <div className={classes.container} {...provided.droppableProps} ref={provided.innerRef}>
                        {options.map((option, index) => (
                            <Draggable key={option.slug} draggableId={option.slug} index={index}>
                                {(provided, snapshot) => (
                                    <DragLayer snapshot={snapshot} provided={provided}>
                                        <OptionItem
                                            option={option}
                                            onChangeText={handleValueChange}
                                            provided={provided}
                                            defaultValues={isMulti ? multiDefaultValues : [singleDefaultValue]}
                                            onCheckboxChange={toggleDefault}
                                            removeOption={removeOption}
                                        />
                                    </DragLayer>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        <Button
                            variant="tertiary"
                            className={classes.addButton}
                            onClick={addOption}
                            leftSection={<Icon icon="plus" />}
                        >
                            Add option
                        </Button>
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    )
}
